import useMediaQuery from '@mui/material/useMediaQuery'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// ** Layout Imports
import Layout from 'src/@core/layouts/Layout'

// ** Navigation Imports
import VerticalNavItems from 'src/navigation/vertical'
import HorizontalNavItems from 'src/navigation/horizontal'

// ** Component Import
import VerticalAppBarContent from './components/vertical/AppBarContent'
import HorizontalAppBarContent from './components/horizontal/AppBarContent'
import AfterVerticalNavMenuContent from './components/vertical/AfterVerticalNavMenuContent'

// ** Hook Import
import { useSettings } from 'src/@core/hooks/useSettings'
import { useAuth } from 'src/hooks/useAuth'
import { useLocation } from 'react-router-dom'
import { isGuestQmsRoute } from 'src/utils/guestQms'
import GuestQmsLayout from './GuestQmsLayout'

// ** API
import { axiosInstance } from 'src/network/adapter'
import { ApiEndPoints } from 'src/network/endpoints'
import { toastError } from 'src/utils/utils'

const UserLayout = ({ children }) => {
  // ** Hooks
  const { settings, saveSettings } = useSettings()
  const { t } = useTranslation()
  const { user, rolePermissions, setRolePermission } = useAuth()
  const location = useLocation()
  const isGuestQms = isGuestQmsRoute(location.pathname, location.search)

  // ** State to track loading
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true)

  const hidden = useMediaQuery(theme => theme.breakpoints.down('lg'))

  const fetchRolesAndPermissions = () => {
    axiosInstance
      .get(ApiEndPoints.ROLE.listById(user?.roleId?._id))
      .then(response => {
        const responseData = response.data.data.role.permissions
        setRolePermission(responseData)
      })
      .catch(error => {
        toastError(error)
      })
      .finally(() => {
        setIsLoadingPermissions(false)
      })
  }

  useEffect(() => {
    if (user?.role === 'subCustomer') {
      fetchRolesAndPermissions()
    } else {
      setIsLoadingPermissions(false)
    }
  }, [user?.role, user?.roleId?._id])

  const getAccessibleNavItems = navItems => {
    // If not a subCustomer, return all nav items
    if (user?.role !== 'subCustomer') {
      console.log('✓ Returning all nav items (not subCustomer)')
      return navItems
    }

    // If permissions haven't loaded yet or are undefined, return only dashboard
    if (!rolePermissions || !Array.isArray(rolePermissions)) {
      return navItems.filter(item => item.id === 'dashboard' || item.title === 'Dashboard')
    }

    // If permissions array is empty, show only dashboard
    if (rolePermissions.length === 0) {
      return navItems.filter(item => item.id === 'dashboard' || item.title === 'Dashboard')
    }

    // Filter nav items based on permissions
    const filteredItems = navItems
      .map(item => {
        // Always allow Dashboard
        if (item.id === 'dashboard' || item.title === 'Dashboard') {
          return item
        }

        // Items with null/undefined permission are accessible to all (like Dispute Center)
        if (item.permission === null || item.permission === undefined) {
          return item
        }

        // Check if user has the required permission (exact match, no normalization)
        const hasPermission = rolePermissions.includes(item.permission)
        return hasPermission ? item : null
      })
      .filter(Boolean)
    return filteredItems
  }

  // Show loading state while permissions are being fetched
  if (user?.role === 'subCustomer' && isLoadingPermissions) {
    return (
      <Layout
        hidden={hidden}
        settings={settings}
        saveSettings={saveSettings}
        {...(settings.layout === 'horizontal'
          ? {
              horizontalNavItems: [],
              horizontalAppBarContent: () => (
                <HorizontalAppBarContent hidden={hidden} settings={settings} saveSettings={saveSettings} />
              )
            }
          : {
              verticalNavItems: VerticalNavItems(t).filter(item => item.id === 'dashboard'),
              afterVerticalNavMenuContent: AfterVerticalNavMenuContent,
              verticalAppBarContent: props => (
                <VerticalAppBarContent
                  hidden={hidden}
                  settings={settings}
                  saveSettings={saveSettings}
                  toggleNavVisibility={props.toggleNavVisibility}
                />
              )
            })}
      >
        {children}
      </Layout>
    )
  }

  const accessibleNavItems = getAccessibleNavItems(VerticalNavItems(t))

  if (isGuestQms) {
    return <GuestQmsLayout>{children}</GuestQmsLayout>
  }

  return (
    <Layout
      hidden={hidden}
      settings={settings}
      saveSettings={saveSettings}
      {...(settings.layout === 'horizontal'
        ? {
            horizontalNavItems: HorizontalNavItems(),
            horizontalAppBarContent: () => (
              <HorizontalAppBarContent hidden={hidden} settings={settings} saveSettings={saveSettings} />
            )
          }
        : {
            verticalNavItems: accessibleNavItems,
            afterVerticalNavMenuContent: AfterVerticalNavMenuContent,
            verticalAppBarContent: props => (
              <VerticalAppBarContent
                hidden={hidden}
                settings={settings}
                saveSettings={saveSettings}
                toggleNavVisibility={props.toggleNavVisibility}
              />
            )
          })}
    >
      {children}
    </Layout>
  )
}

export default UserLayout
